import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import PendingApprovalPage from '../pages/PendingApprovalPage';
import { useAuth } from './AuthContext';

/** True for an auth-class failure of the `me` check — an expired / invalid /
 *  revoked session (401/403 or a UNAUTHORIZED/FORBIDDEN coded error) — as opposed
 *  to a transient network/server error we shouldn't log the user out over. */
function isAuthError(err: unknown): boolean {
  const e = err as { status?: number; info?: { message?: unknown; code?: number } } | null;
  const status = e?.status ?? e?.info?.code;
  if (status === 401 || status === 403) return true;
  const msg =
    (typeof e?.info?.message === 'string' ? e.info.message : undefined) ??
    (err instanceof Error ? err.message : '');
  return msg.includes('UNAUTHORIZED') || msg.includes('FORBIDDEN');
}

export function ProtectedRoute() {
  const { token, setToken } = useAuth();
  const location = useLocation();
  const crud = useClientQueries(schema);
  const {
    data: user,
    isLoading,
    isError,
    error,
  } = crud.$procs.me.useQuery(undefined, {
    enabled: !!token,
    // A dead session (401/403) must fail fast to the login redirect instead of
    // spinning through the default retries; only retry genuine transient errors.
    retry: (count, err) => !isAuthError(err) && count < 2,
  });

  // A token that no longer resolves to a user (expired/invalid) is a dead session:
  // drop it so authFetch stops sending it and the app settles on a clean login
  // state rather than hanging.
  useEffect(() => {
    if (isError && isAuthError(error)) setToken(null);
  }, [isError, error, setToken]);

  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  // `/me` failed while a token was present. Previously this fell through to
  // `!user` and returned null forever — the blank screen on an expired session.
  // Send the user to login instead (an auth error also cleared the token above).
  if (isError) return <Navigate to="/login" replace state={{ from: location }} />;
  // Wait for /me before deciding — otherwise an approved user briefly sees the pending page on refresh.
  if (isLoading || !user) return null;
  if ((user as { approved?: boolean }).approved !== true) return <PendingApprovalPage />;
  return <Outlet />;
}
